import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DynamicField from '../../components/DynamicField';
import api from '../../utils/api';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

const FormFill = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchForm();
  }, [id]);

  const fetchForm = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/forms/${id}`);
      setForm(response.data.form);

      // Initialize responses with empty values
      const initialResponses = {};
      response.data.form.fields.forEach(field => {
        initialResponses[field.key] = field.type === 'multientry' ? [''] : '';
      });
      setResponses(initialResponses);
    } catch (error) {
      showSnackbar(error.response?.data?.error || t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFieldChange = (key, value) => {
    setResponses({ ...responses, [key]: value });
    // Clear error for this field
    if (errors[key]) {
      setErrors({ ...errors, [key]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    form.fields.forEach(field => {
      if (field.required) {
        const value = responses[field.key];

        if (!value || (Array.isArray(value) && value.filter(v => v).length === 0)) {
          newErrors[field.key] = t('survey.requiredField');
        } else if (field.type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            newErrors[field.key] = t('survey.invalidEmail');
          }
        } else if (field.type === 'phone' && value) {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ''))) {
            newErrors[field.key] = t('survey.invalidPhone');
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar('Please fix the errors in the form', 'error');
      return;
    }

    setSubmitting(true);

    try {
      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('formId', id);

      // Collect non-file responses
      const nonFileResponses = {};

      // Handle file uploads
      form.fields.forEach(field => {
        if (field.type === 'image' && responses[field.key]) {
          const files = responses[field.key];
          if (files instanceof FileList) {
            Array.from(files).forEach((file, index) => {
              formData.append('images', file);
            });
          } else if (files instanceof File) {
            formData.append('images', files);
          }
        } else {
          nonFileResponses[field.key] = responses[field.key];
        }
      });

      formData.append('responses', JSON.stringify(nonFileResponses));

      // Get geolocation if available
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
          formData.append('latitude', position.coords.latitude);
          formData.append('longitude', position.coords.longitude);
        } catch (error) {
          console.log('Geolocation not available');
        }
      }

      await api.post('/submissions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      showSnackbar(t('submissions.submissionCreated'));
      setTimeout(() => navigate('/forms'), 2000);
    } catch (error) {
      showSnackbar(error.response?.data?.error || t('common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!form) {
    return (
      <Layout>
        <Alert severity="error">Form not found</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {form.formName}
        </Typography>
        {form.description && (
          <Typography variant="body1" color="text.secondary">
            {form.description}
          </Typography>
        )}
      </Box>

      {submitting && <LinearProgress sx={{ mb: 2 }} />}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" flexDirection="column" gap={3}>
            {form.fields.map((field, index) => (
              <DynamicField
                key={field.key}
                field={field}
                value={responses[field.key]}
                onChange={(value) => handleFieldChange(field.key, value)}
                error={errors[field.key]}
              />
            ))}
          </Box>

          <Box mt={4} display="flex" gap={2}>
            <Button
              variant="contained"
              type="submit"
              size="large"
              startIcon={<SendIcon />}
              disabled={submitting}
            >
              {submitting ? t('common.loading') : t('common.submit')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/forms')}
              disabled={submitting}
            >
              {t('common.cancel')}
            </Button>
          </Box>
        </Paper>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default FormFill;
